import React, { createContext, useContext, useState, useEffect } from 'react';
import { categoryService } from '../services/categoryService';
import {
  Category,
  CategoryWithMonthData,
  CreateCategoryData,
  UpdateCategoryData,
  CreateCategoryMonthData,
  UpdateCategoryMonthData,
  BulkPositionUpdateData,
} from '../types/category';
import { usePlans } from './PlanContext';
import { useAccounts } from './AccountContext';

interface BudgetStatus {
  total_account_balance: number;
  total_remaining_category_balance: number;
  unallocated_amount: number;
  underfunded_amount: number;
  status: 'unallocated' | 'underfunded' | 'balanced';
}

interface CategoryContextType {
  categories: CategoryWithMonthData[];
  loading: boolean;
  error: string | null;
  totalAllocated: number;
  availablePool: number;
  budgetStatus: BudgetStatus | null; // PERFORMANCE: Budget status from combined call
  createCategory: (data: CreateCategoryData) => Promise<Category>;
  createCategoryMonth: (data: CreateCategoryMonthData) => Promise<void>;
  updateCategory: (categoryId: string, data: UpdateCategoryData) => Promise<void>;
  updateCategoryMonth: (categoryMonthId: string, data: UpdateCategoryMonthData) => Promise<void>;
  updateCategoryPositions: (data: BulkPositionUpdateData, reorderedCategories: CategoryWithMonthData[]) => Promise<void>;
  checkCategoryTransactions: (categoryId: string, currentMonthPlanId: string) => Promise<{ hasTransactions: boolean; transactionCount: number }>;
  deleteCategory: (categoryId: string, currentMonthPlanId: string) => Promise<void>;
  refreshCategories: () => Promise<void>;
  refreshBudgetStatusOnly: () => Promise<void>; // PERFORMANCE: Refresh only budget status without full reload
  updateCategoryAllocatedLocally: (categoryMonthId: string, newAllocated: number) => void; // Optimistic update for allocation
  applyAllocationToFutureMonths: (categoryId: string, planId: string, currentMonthPlanId: string, sameAllocation: boolean, currentAllocated: number) => Promise<{ updated_count: number }>;
  validatePoolAllocation: (amount: number, excludeCategoryMonthId?: string) => boolean;
  calculateAvailable: () => number;
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

export const CategoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [categories, setCategories] = useState<CategoryWithMonthData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [budgetStatus, setBudgetStatus] = useState<BudgetStatus | null>(null); // PERFORMANCE: Add budget status

  const { currentMonthPlan } = usePlans();
  const { totalPool, refreshAccounts } = useAccounts();

  const availablePool = totalPool - totalAllocated;

  // Fetch categories when month plan changes
  useEffect(() => {
    if (currentMonthPlan?.id) {
      refreshCategories();
    } else {
      setCategories([]);
      setTotalAllocated(0);
    }
  }, [currentMonthPlan?.id]);

  // Calculate total allocated whenever categories change
  useEffect(() => {
    const total = categories.reduce((sum, group) => {
      const groupTotal = group.children.reduce((childSum, child) => {
        return childSum + (child.allocated || 0);
      }, 0);
      return sum + groupTotal;
    }, 0);
    setTotalAllocated(total);
  }, [categories]);

  const refreshCategories = async () => {
    if (!currentMonthPlan?.id) {
      setCategories([]);
      setBudgetStatus(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // PERFORMANCE: Use combined endpoint to fetch categories AND budget status in one call
      const data = await categoryService.getCategoriesWithBudgetStatus(currentMonthPlan.id);
      setCategories(data.categories);
      setBudgetStatus(data.budget_status);
    } catch (err: any) {
      if (err instanceof Error && err.message === 'Authentication token not available') {
        return;
      }
      setError(err.response?.data?.detail || err?.message || 'Failed to load categories');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  // PERFORMANCE: Refresh only budget status without triggering loading state
  // Used after inline allocation edits to avoid full page reload
  const refreshBudgetStatusOnly = async () => {
    if (!currentMonthPlan?.id) return;

    try {
      const status = await categoryService.getBudgetStatus(currentMonthPlan.id);
      setBudgetStatus(status);
    } catch (err: any) {
      console.error('Error refreshing budget status:', err);
      // Don't set error state - this is a background refresh
    }
  };

  // PERFORMANCE: Optimistically update category allocated value in context
  // Used for inline allocation edits to immediately reflect changes
  const updateCategoryAllocatedLocally = (categoryMonthId: string, newAllocated: number) => {
    setCategories(prevCategories => 
      prevCategories.map(group => ({
        ...group,
        children: group.children.map(child => 
          child.category_month_id === categoryMonthId
            ? { 
                ...child, 
                allocated: newAllocated,
                balance: newAllocated - (child.transaction || 0)
              }
            : child
        )
      }))
    );
  };

  const createCategory = async (data: CreateCategoryData): Promise<Category> => {
    setLoading(true);
    setError(null);
    try {
      const newCategory = await categoryService.createCategory(data);
      await refreshCategories();
      return newCategory;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create category');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createCategoryMonth = async (data: CreateCategoryMonthData) => {
    setLoading(true);
    setError(null);
    try {
      await categoryService.createCategoryMonth(data);
      await refreshCategories();
      await refreshAccounts(); // Refresh accounts to update pool if needed
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create category allocation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCategory = async (categoryId: string, data: UpdateCategoryData) => {
    setLoading(true);
    setError(null);
    try {
      await categoryService.updateCategory(categoryId, data);
      await refreshCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update category');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCategoryMonth = async (
    categoryMonthId: string,
    data: UpdateCategoryMonthData
  ) => {
    setLoading(true);
    setError(null);
    try {
      await categoryService.updateCategoryMonth(categoryMonthId, data);
      await refreshCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update category allocation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCategoryPositions = async (data: BulkPositionUpdateData, reorderedCategories: CategoryWithMonthData[]) => {
    // Store previous state for rollback
    const previousCategories = categories;

    try {
      // CRITICAL FIX: Wait for next animation frame BEFORE optimistic update
      // This allows @hello-pangea/dnd to finish its internal cleanup (isDragging → false)
      // before React re-renders with the new state. Double RAF ensures we're after library cleanup.
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      // Now update - library has finished cleanup
      setCategories(reorderedCategories);

      // Save to backend
      await categoryService.updatePositions(data);
    } catch (err: any) {
      // Rollback on error
      setCategories(previousCategories);
      throw new Error(err.response?.data?.detail || 'Failed to update category positions');
    }
  };

  const checkCategoryTransactions = async (categoryId: string, currentMonthPlanId: string): Promise<{ hasTransactions: boolean; transactionCount: number }> => {
    try {
      const response = await categoryService.checkCategoryTransactions(categoryId, currentMonthPlanId);
      return {
        hasTransactions: response.has_transactions,
        transactionCount: response.transaction_count
      };
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to check category transactions');
    }
  };

  const deleteCategory = async (categoryId: string, currentMonthPlanId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await categoryService.deleteCategory(categoryId, currentMonthPlanId);
      await refreshCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete category');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const applyAllocationToFutureMonths = async (
    categoryId: string,
    planId: string,
    currentMonthPlanId: string,
    sameAllocation: boolean,
    currentAllocated: number
  ): Promise<{ updated_count: number }> => {
    try {
      const result = await categoryService.applyAllocationToFutureMonths(
        categoryId,
        planId,
        currentMonthPlanId,
        sameAllocation,
        currentAllocated
      );
      // Refresh categories to show updated data
      await refreshCategories();
      return result;
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to apply allocation to future months');
    }
  };

  const validatePoolAllocation = (amount: number, excludeCategoryMonthId?: string): boolean => {
    // Calculate current allocated (excluding the one being updated if provided)
    let currentAllocated = 0;
    categories.forEach((group) => {
      group.children.forEach((child) => {
        // Skip the category month being updated
        if (excludeCategoryMonthId && child.id === excludeCategoryMonthId) {
          return;
        }
        currentAllocated += child.allocated || 0;
      });
    });

    const newTotal = currentAllocated + amount;
    return newTotal <= totalPool;
  };

  const calculateAvailable = (): number => {
    return totalPool - totalAllocated;
  };

  return (
    <CategoryContext.Provider
      value={{
        categories,
        loading,
        error,
        totalAllocated,
        availablePool,
        budgetStatus, // PERFORMANCE: Expose budget status from combined call
        createCategory,
        createCategoryMonth,
        updateCategory,
        updateCategoryMonth,
        updateCategoryPositions,
        checkCategoryTransactions,
        deleteCategory,
        refreshCategories,
        refreshBudgetStatusOnly, // PERFORMANCE: Only refresh budget status
        updateCategoryAllocatedLocally, // PERFORMANCE: Optimistic update for allocation
        applyAllocationToFutureMonths,
        validatePoolAllocation,
        calculateAvailable,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};
